import { useContext, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { MuiOtpInput } from 'mui-one-time-password-input'

import Dialog from '../common/dialog'
import { FormError } from '../../types'
import {sendCryptoFunds, resolveDocumentsAccessToken } from '../../api/documents.api'
import { AuthContext, NotificationsContext } from '../../contexts'
import { parseNumber } from '../customer-companies-management/utils'
import { checkTwoFactorAuth, requestAccessToken } from '../../api/auth.api'
import { handleCheckIfOtpCodeIsValid, numberWithCommas } from '../../utils'

export const ConfirmTransferCryptoFundsModal = ({
  walletToUse,
  feeToPayInUSD,
  feeToPayInTRX,
  feeToPayInETH,
  selectedNetwork,
  walletAddressToSendTo,
  amountToTransferValue,
  handleOpenStateOfModal
}: {
  walletToUse: string
  feeToPayInUSD: number
  feeToPayInETH: number
  feeToPayInTRX: number
  selectedNetwork: string
  walletAddressToSendTo: string
  amountToTransferValue: string
  handleOpenStateOfModal: (openState: boolean, cryptoPaymentSent?: boolean) => void
}) => {
  const navigate = useNavigate()
  const { isAuth, removeAuth } = useContext(AuthContext)
  const { t } = useTranslation(['crypto-management', 'common'])
  const { updateNewNotificationStatus } = useContext(NotificationsContext)

  const [otpCode, setOtpCode] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [errors, setErrors] = useState<FormError | undefined>(undefined)

  const handleSendCryptoFunds = async () => {
    const { otpCodeIsValid, formErrors } = handleCheckIfOtpCodeIsValid(otpCode, t)

    if (!otpCodeIsValid) {
      setErrors({ ...formErrors })
      return
    }

    try {
      setIsLoading(true)
      const {
        data: { accessToken }
      } = await requestAccessToken(isAuth?.replaceAll('"', '') ?? '')
      await checkTwoFactorAuth(otpCode, accessToken)
    } catch (error: any) {
      console.log(error)
      if (error?.response?.status === 401 && error?.response?.data?.message === 'Not Authorized: Expired') {
        handleCloseSession()
      } else if (error?.response?.status === 401) {
        setErrors({ generalError: t('formGeneralErrors.codeInvalid', { ns: 'common' }) })
      } else if (error?.response?.status === 404) {
        setErrors({ generalError: t('formGeneralErrors.codeExpired', { ns: 'common' }) })
      } else {
        setErrors({ generalError: t('formGeneralErrors.serverError', { ns: 'common' }) })
      }

      setIsLoading(false)
      return
    }

    try {
      setErrors({})
      setOtpCode('')
      const accessToken = await resolveDocumentsAccessToken(isAuth?.replaceAll('"', '') ?? '')
      await sendCryptoFunds({ walletToUse, walletAddressToSendTo, amountToTransferValue, feeToPayInTRX, selectedNetwork, feeToPayInETH }, accessToken)

      handleOpenStateOfModal(false, true)
      updateNewNotificationStatus?.(true, 'crypto-payment-sent')
    } catch (error: any) {
      console.log(error)
      if (error?.response?.status === 401 && error?.response?.data?.message === 'Not Authorized: Expired') {
        handleCloseSession()
      } else if (error?.response?.status === 401) {
        setErrors({ generalError: t('formGeneralErrors.codeInvalid', { ns: 'common' }) })
      } else if (error?.response?.status === 404) {
        setErrors({ generalError: t('formGeneralErrors.codeExpired', { ns: 'common' }) })
      } else {
        setErrors({ generalError: t('formGeneralErrors.serverError', { ns: 'common' }) })
      }

      setIsLoading(false)
      return
    }
  }

  const handleCloseSession = () => {
    removeAuth?.()
    navigate('/', { replace: true, state: { sessionExpired: true } })
  }

  return (
    <Dialog
      errors={errors}
      actionButtonSuccessStyle
      dialogIsLoading={isLoading}
      handleDialogAction={handleSendCryptoFunds}
      dialogTitle={t('modal.confirmSendCryptoFunds.title')}
      actionButtonText={t('modal.confirmSendCryptoFunds.actionButtonText')}
      handleDialogOpenState={(openState: boolean) => {
        handleOpenStateOfModal(openState)

        if (openState === false) {
          setErrors(undefined)
        }
      }}
    >
      <div
        style={{ textAlign: 'center', lineHeight: '150%' }}
        className="company-customer-funding-modal-main-instruction"
      >{`${t('modal.confirmSendCryptoFunds.mainInstruction')}:`}</div>
      <div className="company-customer-funding-content-container">
        <div className="trade-module-container">
          <div
            style={{ gap: '15px' }}
            className="operation-container"
          >
            <div
              className="operation-title crypto-operation-title"
              style={{ display: 'flex', width: '100%', flexDirection: 'column', gap: '8px', justifyContent: 'space-between' }}
            >
              <div>{t('modal.confirmSendCryptoFunds.amountToSend')}:</div>
              {selectedNetwork === 'tron' || selectedNetwork === 'tron-paxum' || selectedNetwork === 'tron-wpagos' ? (
                <div
                  className="native-balance-text"
                  style={{ fontWeight: '700', textAlign: 'right', color: 'rgb(46, 211, 183)' }}
                >
                  {`${amountToTransferValue} USDT`}
                </div>
              ) : (
                <div
                  className="native-balance-text"
                  style={{ fontWeight: '700', textAlign: 'right', color: 'rgb(46, 211, 183)' }}
                >
                  {`${amountToTransferValue} USDC`}
                </div>
              )}
            </div>
            <div
              className="operation-title crypto-operation-title"
              style={{ display: 'flex', width: '100%', flexDirection: 'column', gap: '8px', justifyContent: 'space-between' }}
            >
              <div>{t('modal.confirmSendCryptoFunds.walletAddressToSendTo')}:</div>
              <div
                className="native-balance-text"
                style={{ fontWeight: '700', textAlign: 'right', color: 'rgb(46, 211, 183)' }}
              >
                {walletAddressToSendTo}
              </div>
            </div>
            <div
              className="operation-title crypto-operation-title"
              style={{ display: 'flex', width: '100%', flexDirection: 'column', gap: '8px', justifyContent: 'space-between' }}
            >
              <div>{t('modal.confirmSendCryptoFunds.feeToPay')}:</div>
              {selectedNetwork === 'tron' || selectedNetwork === 'tron-paxum' || selectedNetwork === 'tron-wpagos' ? (
                <div
                  className="native-balance-text"
                  style={{ fontWeight: '700', textAlign: 'right', color: 'rgb(46, 211, 183)' }}
                >
                  {`${parseNumber(Number(feeToPayInTRX))} TRX ≈ $ ${numberWithCommas(Number(feeToPayInUSD).toFixed(2))} USD`}
                </div>
              ) : (
                <div
                  className="native-balance-text"
                  style={{ fontWeight: '700', textAlign: 'right', color: 'rgb(46, 211, 183)' }}
                >
                  {`${parseNumber(Number(feeToPayInETH))} ETH ≈ $ ${numberWithCommas(Number(feeToPayInUSD).toFixed(2))} USD`}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      <div
        className="company-customer-funding-modal-main-instruction"
        style={{ color: '#F59E0B', fontWeight: '600', textAlign: 'center', lineHeight: '150%', marginTop: '24px' }}
      >{`${t('modal.confirmSendCryptoFunds.insertYour2FA')}:`}</div>
      <MuiOtpInput
        length={6}
        value={otpCode}
        onChange={(newValue: string) => setOtpCode(newValue)}
        className={errors?.otpCodeError ? 'MuiOtpInput-Box-inner MuiOtpInput-Box-error' : 'MuiOtpInput-Box-inner'}
      />
    </Dialog>
  )
}
